import { useState, type ReactNode } from 'react';
import { reportsApi } from '../../api/services';
import type { FinancialStatementDto, TrialBalanceRowDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Check, Field, Input, Loadable, NumberInput, PageHeader, Pager, Select, Table, Tabs, Toolbar,
  firstOfMonthIso, fmtDate, fmtMoney, money, todayIso, useAction, useAsync,
} from '../../ui/components';
import { useCurrencies } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { CashFlowReport, EquityReport } from './ExtraStatements';

type Tab = 'trial' | 'journal' | 'ledger' | 'balance' | 'income' | 'cashflow' | 'equity' | 'catalog';

export function ReportsPage() {
  const [tab, setTab] = useState<Tab>('trial');
  return (
    <>
      <PageHeader title="Reportes contables" subtitle="Incluyen asientos aplicados y anulados (cada anulación tiene su reverso). Se filtran por fecha del asiento." />
      <PageHelp page="reports" />
      <Tabs<Tab> tabs={[{ key: 'trial', label: 'Balance de comprobación' }, { key: 'journal', label: 'Libro diario' }, { key: 'ledger', label: 'Libro mayor / auxiliar' }, { key: 'balance', label: 'Balance general' }, { key: 'income', label: 'Estado de resultados' }, { key: 'cashflow', label: 'Flujo de efectivo' }, { key: 'equity', label: 'Cambios en el patrimonio' }, { key: 'catalog', label: 'Catálogo' }]} active={tab} onChange={setTab} />
      {tab === 'trial' && <TrialBalance />}
      {tab === 'journal' && <Journal />}
      {tab === 'ledger' && <Ledger />}
      {tab === 'balance' && <Statement kind="balance" />}
      {tab === 'income' && <Statement kind="income" />}
      {tab === 'cashflow' && <CashFlowReport />}
      {tab === 'equity' && <EquityReport />}
      {tab === 'catalog' && <Catalog />}
    </>
  );
}

function CurrencyFilter({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  const { data } = useCurrencies();
  return <Select value={value ?? ''} onChange={(v) => onChange((v as unknown) === '' ? null : v)} placeholder="Moneda base" options={(data ?? []).filter((c) => c.mapped).map((c) => ({ value: c.id, label: `${c.name} (originales)` }))} />;
}

function CsvButton({ report, query, name }: { report: string; query: Record<string, string | number | boolean | undefined>; name: string }) {
  const { run, busy } = useAction();
  return <Button busy={busy} onClick={() => run(() => reportsApi.csv(report, query, name))}>Exportar CSV</Button>;
}

function useRangeFilters() {
  const [f, setF] = useState({ dateFrom: firstOfMonthIso(), dateTo: todayIso(), currencyId: null as number | null, level: null as number | null, accountFrom: '', accountTo: '', includeZero: false });
  const query = { dateFrom: f.dateFrom, dateTo: f.dateTo, currencyId: f.currencyId ?? undefined, level: f.level ?? undefined, accountFrom: f.accountFrom || undefined, accountTo: f.accountTo || undefined, includeZero: f.includeZero || undefined };
  const bar = (extra?: ReactNode) => (
    <Toolbar>
      <Field label="Desde"><Input type="date" value={f.dateFrom} onChange={(v) => setF({ ...f, dateFrom: v })} /></Field>
      <Field label="Hasta"><Input type="date" value={f.dateTo} onChange={(v) => setF({ ...f, dateTo: v })} /></Field>
      <Field label="Moneda"><CurrencyFilter value={f.currencyId} onChange={(v) => setF({ ...f, currencyId: v })} /></Field>
      <Field label="Nivel"><NumberInput step="1" min={1} value={f.level} onChange={(v) => setF({ ...f, level: v || null })} /></Field>
      <Field label="Cuenta desde"><Input value={f.accountFrom} onChange={(v) => setF({ ...f, accountFrom: v })} /></Field>
      <Field label="Cuenta hasta"><Input value={f.accountTo} onChange={(v) => setF({ ...f, accountTo: v })} /></Field>
      <Check checked={f.includeZero} onChange={(v) => setF({ ...f, includeZero: v })} label="Incluir sin saldo" />
      {extra}
    </Toolbar>
  );
  return { f, query, bar };
}

function TrialBalance() {
  const { query, bar } = useRangeFilters();
  const state = useAsync(() => reportsApi.trialBalance(query), [JSON.stringify(query)]);
  return (
    <>
      {bar(<CsvButton report="trial-balance" query={query} name="balance-comprobacion.csv" />)}
      <Loadable state={state}>
        {(r) => (
          <Card title={`${r.company} · ${fmtDate(r.dateFrom)} al ${fmtDate(r.dateTo)} · ${r.currency}`}>
            {r.balanced ? <Badge tone="ok">Cuadrado</Badge> : <Alert kind="warn">Los totales no cuadran en este filtro (esperado si hay filtro de cuentas o moneda original).</Alert>}
            <Table rows={r.rows} rowKey={(x) => x.accountNumber} columns={[
              { key: 'n', title: 'Cuenta', render: (x) => <code>{x.accountNumber}</code> },
              { key: 'm', title: 'Nombre', render: (x) => <span style={{ paddingLeft: (x.level - 1) * 12, fontWeight: x.accountType === 'M' ? 600 : 400 }}>{x.accountName}</span> },
              money<TrialBalanceRowDto>('o', 'Saldo inicial', (x) => x.openingBalance),
              money<TrialBalanceRowDto>('d', 'Débitos', (x) => x.debit),
              money<TrialBalanceRowDto>('c', 'Créditos', (x) => x.credit),
              money<TrialBalanceRowDto>('s', 'Saldo final', (x) => x.closingBalance),
            ]} footer={<tr><td colSpan={2}><strong>Totales (cuentas de detalle)</strong></td>{[r.totalOpening, r.totalDebit, r.totalCredit, r.totalClosing].map((v, i) => <td key={i} style={{ textAlign: 'right' }}><strong>{fmtMoney(v)}</strong></td>)}</tr>} />
          </Card>
        )}
      </Loadable>
    </>
  );
}

function Journal() {
  const [f, setF] = useState({ dateFrom: firstOfMonthIso(), dateTo: todayIso(), status: '', page: 1 });
  const state = useAsync(() => reportsApi.journal({ ...f, status: f.status || undefined, pageSize: 25 }), [f]);
  return (
    <>
      <Toolbar>
        <Field label="Desde"><Input type="date" value={f.dateFrom} onChange={(v) => setF({ ...f, dateFrom: v, page: 1 })} /></Field>
        <Field label="Hasta"><Input type="date" value={f.dateTo} onChange={(v) => setF({ ...f, dateTo: v, page: 1 })} /></Field>
        <Field label="Estado"><Select value={f.status} onChange={(v) => setF({ ...f, status: v, page: 1 })} placeholder="Aplicados y anulados" options={[{ value: 'Apli', label: 'Aplicados' }, { value: 'Anu', label: 'Anulados' }, { value: 'A', label: 'Abiertos' }]} /></Field>
        <CsvButton report="journal" query={{ dateFrom: f.dateFrom, dateTo: f.dateTo, status: f.status || undefined }} name="libro-diario.csv" />
      </Toolbar>
      <Loadable state={state}>
        {(r) => (
          <>
            {r.items.map((e) => (
              <Card key={e.entryCode} title={`${e.entryCode} · ${fmtDate(e.date)} · ${e.description}`} actions={<Badge tone={e.status === 'Apli' ? 'ok' : e.status === 'Anu' ? 'bad' : 'warn'}>{e.status}</Badge>}>
                <Table rows={e.lines} rowKey={(l) => l.line} columns={[
                  { key: 'l', title: '#', render: (l) => l.line }, { key: 'a', title: 'Cuenta', render: (l) => `${l.accountNumber} ${l.accountName ?? ''}` },
                  { key: 'd', title: 'Débito', align: 'right', render: (l) => (l.debit ? fmtMoney(l.debit) : '') }, { key: 'c', title: 'Crédito', align: 'right', render: (l) => (l.credit ? fmtMoney(l.credit) : '') },
                  { key: 'o', title: 'Original', align: 'right', render: (l) => `${fmtMoney(l.originalAmount)} ${l.originalCurrency}` },
                ]} />
              </Card>
            ))}
            <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={(p) => setF({ ...f, page: p })} />
          </>
        )}
      </Loadable>
    </>
  );
}

function Ledger() {
  const { query, bar } = useRangeFilters();
  const [account, setAccount] = useState('');
  const [customer, setCustomer] = useState('');
  const q = { ...query, accountNumber: account.trim() || undefined, customerCode: customer.trim() || undefined };
  const state = useAsync(() => reportsApi.ledger(q), [JSON.stringify(q)]);
  return (
    <>
      {bar(<>
        <Field label="Cuenta (auxiliar)"><Input value={account} onChange={setAccount} placeholder="Vacío = rango" /></Field>
        <Field label="Cliente"><Input value={customer} onChange={setCustomer} /></Field>
        <CsvButton report="ledger" query={q} name="libro-mayor.csv" />
      </>)}
      <Loadable state={state}>
        {(r) => (
          <>
            {r.accounts.length === 0 && <Alert>Sin movimientos para el filtro.</Alert>}
            {r.accounts.map((a) => (
              <Card key={a.accountNumber} title={`${a.accountNumber} · ${a.accountName}`} actions={<span>Inicial {fmtMoney(a.openingBalance)} · Final <strong>{fmtMoney(a.closingBalance)}</strong></span>}>
                <Table rows={a.movements} rowKey={(m) => `${m.entryCode}-${m.line}`} columns={[
                  { key: 'f', title: 'Fecha', render: (m) => fmtDate(m.date) },
                  { key: 'a', title: 'Asiento', render: (m) => <a href={`#/entries/${encodeURIComponent(m.entryCode)}`}>{m.entryCode}</a> },
                  { key: 'd', title: 'Descripción', render: (m) => m.description },
                  { key: 'c', title: 'Cliente', render: (m) => m.customerCode ?? '' },
                  { key: 'db', title: 'Débito', align: 'right', render: (m) => (m.debit ? fmtMoney(m.debit) : '') },
                  { key: 'cr', title: 'Crédito', align: 'right', render: (m) => (m.credit ? fmtMoney(m.credit) : '') },
                  { key: 's', title: 'Saldo', align: 'right', render: (m) => fmtMoney(m.balance) },
                ]} />
              </Card>
            ))}
          </>
        )}
      </Loadable>
    </>
  );
}

function Statement({ kind }: { kind: 'balance' | 'income' }) {
  const [f, setF] = useState({ dateFrom: kind === 'income' ? `${todayIso().slice(0, 4)}-01-01` : '', dateTo: todayIso(), currencyId: null as number | null, level: null as number | null, includeZero: false });
  const query = { dateFrom: kind === 'income' ? f.dateFrom || undefined : undefined, dateTo: f.dateTo, currencyId: f.currencyId ?? undefined, level: f.level ?? undefined, includeZero: f.includeZero || undefined };
  const state = useAsync<FinancialStatementDto>(() => (kind === 'balance' ? reportsApi.balanceSheet(query) : reportsApi.incomeStatement(query)), [kind, JSON.stringify(query)]);
  return (
    <>
      <Toolbar>
        {kind === 'income' && <Field label="Desde" hint="Vacío = desde el último cierre fiscal"><Input type="date" value={f.dateFrom} onChange={(v) => setF({ ...f, dateFrom: v })} /></Field>}
        <Field label={kind === 'balance' ? 'Al' : 'Hasta'}><Input type="date" value={f.dateTo} onChange={(v) => setF({ ...f, dateTo: v })} /></Field>
        <Field label="Moneda"><CurrencyFilter value={f.currencyId} onChange={(v) => setF({ ...f, currencyId: v })} /></Field>
        <Field label="Nivel"><NumberInput step="1" min={1} value={f.level} onChange={(v) => setF({ ...f, level: v || null })} /></Field>
        <Check checked={f.includeZero} onChange={(v) => setF({ ...f, includeZero: v })} label="Incluir sin saldo" />
        <CsvButton report={kind === 'balance' ? 'balance-sheet' : 'income-statement'} query={query} name={kind === 'balance' ? 'balance-general.csv' : 'estado-resultados.csv'} />
      </Toolbar>
      <Loadable state={state}>
        {(r) => (
          <Card title={`${r.title} · ${r.company} · ${r.dateFrom ? `${fmtDate(r.dateFrom)} al ` : 'al '}${fmtDate(r.dateTo)} · ${r.currency}`}>
            {r.notes.map((n) => <Alert key={n}>{n}</Alert>)}
            {r.unassignedClassifications.length > 0 && <Alert kind="warn">Clasificaciones sin sección (no aparecen): {r.unassignedClassifications.join(', ')}. Asígnelas en Estructura del catálogo.</Alert>}
            {r.sections.map((s) => (
              <div key={s.section} className="acc-statement-section">
                <h4>{s.title}</h4>
                <table className="acc-table acc-table-compact"><tbody>
                  {s.lines.map((l, i) => (
                    <tr key={`${l.accountNumber}-${i}`}><td style={{ paddingLeft: 8 + (l.level - 1) * 14, fontWeight: l.accountType === 'M' ? 600 : 400 }}>{l.accountNumber && <code>{l.accountNumber}</code>} {l.accountName}</td><td className="acc-r">{fmtMoney(l.amount)}</td></tr>
                  ))}
                  <tr className="acc-total"><td>Total {s.title}</td><td className="acc-r">{fmtMoney(s.total)}</td></tr>
                </tbody></table>
              </div>
            ))}
            <table className="acc-table acc-table-compact"><tbody>
              {Object.entries(r.totals).map(([k, v]) => <tr key={k} className={k === 'DIFERENCIA' && Math.abs(v) > 0.004 ? 'acc-bad' : ''}><td>{k.replace(/_/g, ' ')}</td><td className="acc-r"><strong>{fmtMoney(v)}</strong></td></tr>)}
            </tbody></table>
          </Card>
        )}
      </Loadable>
    </>
  );
}

function Catalog() {
  const state = useAsync(() => reportsApi.catalog(), []);
  return (
    <>
      <Toolbar><CsvButton report="catalog" query={{}} name="catalogo-cuentas.csv" /></Toolbar>
      <Loadable state={state}>
        {(rows) => <Table rows={rows} rowKey={(r) => r.accountNumber} columns={[
          { key: 'n', title: 'Cuenta', render: (r) => <code>{r.accountNumber}</code> },
          { key: 'm', title: 'Nombre', render: (r) => <span style={{ paddingLeft: (r.level - 1) * 12 }}>{r.accountName}</span> },
          { key: 't', title: 'Tipo', render: (r) => r.accountType },
          { key: 'c', title: 'Clasificación', render: (r) => r.classification ?? '' },
          { key: 's', title: 'Sección', render: (r) => r.section ?? <Badge tone="warn">sin sección</Badge> },
          { key: 'e', title: 'Estado', render: (r) => r.status ?? '' },
        ]} />}
      </Loadable>
    </>
  );
}
